import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import CompanySearchField from "@/components/CompanySearchField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, CheckCircle2, ExternalLink, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
  "Devis & factures pro en PDF",
  "Agenda & prise de rendez-vous",
  "Clients, véhicules & prestations",
  "Encaissements & comptabilité URSSAF",
  "Recherche entreprise SIRET automatique",
  "Envoi de documents par email",
];

export default function Register() {
  const { setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null); // { verification_link }
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "", confirm: "", phone: "" });
  const [biz, setBiz] = useState({ company: "", siren: "", siret: "", address: "" });
  const [accept, setAccept] = useState(false);

  useEffect(() => { api.get("/platform/public-config").then((r) => setCfg(r.data)).catch(() => setCfg({ signup_enabled: true, invite_required: false, app_name: "Auto Detail Manager", trial_days: 14 })); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const company = (biz.company || document.querySelector('[data-testid="reg-company-search-input"]')?.value || "").trim();
    if (!company) return setError("Le nom de l'entreprise est requis.");
    if (biz.siren && !/^\d{9}$/.test(biz.siren.replace(/\s/g, ""))) return setError("Le SIREN doit comporter 9 chiffres (ou laissez-le vide).");
    if (biz.siret && !/^\d{14}$/.test(biz.siret.replace(/\s/g, ""))) return setError("Le SIRET doit comporter 14 chiffres (ou laissez-le vide).");
    if (f.password.length < 8) return setError("Le mot de passe doit contenir au moins 8 caractères.");
    if (f.password !== f.confirm) return setError("Les mots de passe ne correspondent pas.");
    if (!accept) return setError("Vous devez accepter les CGU et la politique de confidentialité.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        first_name: f.first_name, last_name: f.last_name, email: f.email, password: f.password,
        company_name: company, phone: f.phone || null, siren: biz.siren ? biz.siren.replace(/\s/g, "") : null,
        siret: biz.siret ? biz.siret.replace(/\s/g, "") : null, address: biz.address || null, accept_terms: accept,
      });
      localStorage.setItem("ced_token", data.token);
      await refresh().catch(() => setUser(data.user));
      toast.success("Compte créé — bienvenue !");
      setDone({ verification_link: data.verification_link });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (cfg && !cfg.signup_enabled) {
    return (
      <Shell>
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="signup-closed">
          <h2 className="font-heading text-xl font-bold text-slate-900">Inscriptions fermées</h2>
          <p className="text-sm text-slate-500 mt-2">Les inscriptions ne sont pas ouvertes pour le moment.</p>
          <a href="/login" className="inline-block mt-5 text-emerald-600 hover:underline text-sm">Retour à la connexion</a>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center space-y-4" data-testid="register-done">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
          <h2 className="font-heading text-xl font-bold text-slate-900">Compte créé avec succès</h2>
          <p className="text-sm text-slate-500">Votre essai gratuit de {cfg?.trial_days || 14} jours a démarré.</p>
          {done.verification_link ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left" data-testid="verify-notice">
              <p className="text-sm text-amber-900 font-medium mb-2">Vérification de l'email (mode aperçu)</p>
              <p className="text-xs text-amber-800 mb-3">L'envoi d'email n'est pas actif en aperçu. Cliquez sur ce lien pour vérifier votre adresse :</p>
              <a href={done.verification_link} data-testid="verify-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline break-all"><ExternalLink className="w-4 h-4" /> Vérifier mon adresse email</a>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Un email de vérification vient de vous être envoyé.</p>
          )}
          <Button onClick={() => navigate("/dashboard")} data-testid="btn-continue-dashboard" className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">Continuer vers le tableau de bord</Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-2xl space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prénom"><Input value={f.first_name} onChange={set("first_name")} required data-testid="reg-first-name" className="bg-slate-50 border-slate-200" /></Field>
          <Field label="Nom"><Input value={f.last_name} onChange={set("last_name")} required data-testid="reg-last-name" className="bg-slate-50 border-slate-200" /></Field>
        </div>
        <Field label="Email professionnel"><Input type="email" value={f.email} onChange={set("email")} required data-testid="reg-email" className="bg-slate-50 border-slate-200" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mot de passe"><Input type="password" value={f.password} onChange={set("password")} required data-testid="reg-password" className="bg-slate-50 border-slate-200" /></Field>
          <Field label="Confirmer"><Input type="password" value={f.confirm} onChange={set("confirm")} required data-testid="reg-confirm" className="bg-slate-50 border-slate-200" /></Field>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <CompanySearchField value={biz} onChange={(patch) => setBiz((b) => ({ ...b, ...patch }))} testidPrefix="reg-company" publicSearch />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Téléphone"><Input value={f.phone} onChange={set("phone")} data-testid="reg-phone" className="bg-slate-50 border-slate-200" /></Field>
          <Field label="Adresse"><Input value={biz.address} onChange={(e) => setBiz({ ...biz, address: e.target.value })} data-testid="reg-address" className="bg-slate-50 border-slate-200" /></Field>
        </div>

        <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="reg-accept-terms" className="mt-0.5" />
          <span>J'accepte les <a href="/cgu" className="text-emerald-600 hover:underline">CGU</a> et la <a href="/confidentialite" className="text-emerald-600 hover:underline">politique de confidentialité</a>.</span>
        </label>

        {error && <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="register-error">{error}</div>}

        <Button type="submit" disabled={loading} data-testid="btn-register" className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Créer mon compte {cfg?.trial_days ? `· essai ${cfg.trial_days} j` : ""}
        </Button>
        <a href="/login" data-testid="link-login" className="block text-center text-xs text-slate-500 hover:text-emerald-600">Déjà un compte ? Se connecter</a>
      </form>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.08),transparent_40%),radial-gradient(circle_at_80%_80%,rgba(239,68,68,0.05),transparent_40%)]" />
      <div className="relative w-full max-w-lg">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/20"><ShieldCheck className="w-8 h-8 text-black" /></div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 tracking-tight">Créer mon compte</h1>
          <p className="text-sm text-slate-500 mt-1">Auto Detail Manager — logiciel de gestion pour le detailing</p>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-semibold" data-testid="free-trial-badge">
            <Sparkles className="w-3.5 h-3.5" /> 14 jours gratuits, sans carte bancaire
          </div>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1.5 text-xs text-slate-600 max-w-md" data-testid="register-benefits">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /><span>{b}</span></li>
            ))}
          </ul>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;
}
